"use client";

import { useTheme } from "@/lib/useTheme";
import { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "@/lib/useLanguage";

type CurtainPhase = "idle" | "falling" | "rising";

const WIPE_DURATION_MS = 450;
const WIPE_EASING = "cubic-bezier(0.76, 0, 0.24, 1)";

export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<CurtainPhase>("idle");
  const curtainColorRef = useRef<string>("");

  // Avoid hydration mismatch by only rendering icon after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = useCallback(() => {
    if (phase !== "idle") return;

    const isDarkNow =
      theme === "system" ? resolvedTheme === "dark" : theme === "dark";
    const next: "light" | "dark" = isDarkNow ? "light" : "dark";

    // Curtain color matches the destination background so the page reveals into it
    curtainColorRef.current = next === "dark" ? "#020617" : "#ffffff";
    setPhase("falling");

    window.setTimeout(() => {
      setTheme(next);
      setPhase("rising");
      window.setTimeout(() => setPhase("idle"), WIPE_DURATION_MS + 50);
    }, WIPE_DURATION_MS);
  }, [phase, theme, resolvedTheme, setTheme]);

  const getIcon = () => {
    if (!mounted) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      );
    }
    const isDark = theme === "system" ? resolvedTheme === "dark" : theme === "dark";
    if (isDark) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      );
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  };

  const getLabel = () => {
    const c = t.common;
    const toggleTheme = c?.toggleTheme ?? "Change theme";
    const switchToLight = c?.switchToLightMode ?? "Switch to light mode";
    const switchToDark = c?.switchToDarkMode ?? "Switch to dark mode";

    if (!mounted) {
      return toggleTheme;
    }
    const isDark = theme === "system" ? resolvedTheme === "dark" : theme === "dark";
    return isDark ? switchToLight : switchToDark;
  };

  return (
    <>
      {/* Curtain overlay — drops down on theme change, then retracts upward */}
      {mounted && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            inset: 0,
            background: curtainColorRef.current,
            transformOrigin: phase === "rising" ? "bottom" : "top",
            transform: phase === "falling" ? "scaleY(1)" : "scaleY(0)",
            transition:
              phase === "idle"
                ? "none"
                : `transform ${WIPE_DURATION_MS}ms ${WIPE_EASING}`,
            zIndex: 9997,
            pointerEvents: "none",
          }}
        />
      )}

      <button
        onClick={toggleTheme}
        disabled={phase !== "idle"}
        className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-70"
        title={getLabel()}
        aria-label={getLabel()}
      >
        {getIcon()}
      </button>
    </>
  );
}
